import { useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'

import {
  EMPTY_LIST,
  EMPTY_STRING,
  INPUT_AUTOCOMPLETE_VALUE,
} from '../../constants/common'
import { useApiAbortController, useDebounce } from '../../hooks'
import { getItemsFromIdwellApiResponse } from '../../utils/api'
import { getQaSelector } from '../../utils/common'
import { stopPropagation } from '../../utils/events'
import { BUTTON_HEIGHT_32, ButtonArrow } from '../Button'
import { Dropdown } from '../Dropdown'
import {
  getVerticalIconChevronDirection,
  ICON_HEIGHT_16,
  IconChevron,
} from '../Icon'
import { InputField } from '../Input'

import {
  checkNeedLoadDataOnCustomSelectOpen,
  checkShowCustomSelectListDropdown,
  getLocalSearchOptions,
  getSelectApiMetaData,
} from './CustomSelect.utils'
import { SelectButton } from './SelectButton'
import { SelectList } from './SelectList'
import { SelectValueItem } from './SelectValueItem'

import * as styles from './CustomSelect.module.css'

/**
 * @deprecated Get rid of this component in favor of base selects in `components/Select`
 */
function CustomSelect({
  Button = SelectButton,
  ExtraItem,
  ListItem,
  ValueItem,
  actionsMenu,
  activeItems,
  api,
  apiParams,
  autoComplete = INPUT_AUTOCOMPLETE_VALUE.off,
  autoFocus,
  beforeIconProps,
  buttonClassName,
  buttonHeight = BUTTON_HEIGHT_32,
  buttonOpenClassName = styles.buttonOpen,
  buttonProps,
  buttonType,
  canClear,
  checkNeedLoadDataOnOpen = checkNeedLoadDataOnCustomSelectOpen,
  checkShowListDropdown = checkShowCustomSelectListDropdown,
  className,
  clearable = true,
  count,
  disabled,
  dropdownClassName,
  dropdownMaxWidth,
  entity,
  error,
  errorText,
  extraItemProps,
  filterLocalSearchItems = getLocalSearchOptions,
  getApiData = getItemsFromIdwellApiResponse,
  getApiMeta = getSelectApiMetaData,
  getLabel,
  getListItemProps,
  getValue,
  id,
  initSearchValue,
  inline,
  inputBefore,
  inputHeight = 32,
  inputInfo,
  inputLabel,
  inputTooltip,
  inputType,
  inputValue = EMPTY_STRING,
  isDropdownBottomPosition,
  isMulti = false,
  isSkeleton,
  itemContentClassName,
  listIcon,
  listType,
  localSearch,
  maxHeight = 304,
  menuClassName,
  minSearchString = 0,
  name,
  newItem,
  noDataText,
  openOnFocus = true,
  options: initialOptions = EMPTY_LIST,
  placeholder,
  qaName,
  required,
  scrollListener = true,
  searchKey,
  selectBtnTooltip,
  shouldLoadOnEmptyString = true,
  showBtnTextTooltip,
  showError,
  showLabel,
  showListTooltip = true,
  tags,
  textClassName,
  updateRect,
  value,
  valueItemProps,
  onChange,
  onChangeInput,
  onClear,
  onClearInput,
  onClick,
  onClose,
  onEdit,
}) {
  const [listHeight, setListHeight] = useState(null)
  const [meta, setMeta] = useState(null)
  const [rect, setRect] = useState(null)
  const [options, setOptions] = useState(EMPTY_LIST)
  const [localOptions, setLocalOptions] = useState(initialOptions)
  const [search, setSearch] = useState(
    initSearchValue === undefined ? inputValue : initSearchValue
  )
  const [loadPage, setLoadPage] = useState(null)

  const [isInitiated, setInitiated] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [isOpen, setOpen] = useState(false)

  const debouncedSearch = useDebounce(search, 500)

  const selectRef = useRef(null)
  const buttonRef = useRef(null)

  const apiAbortController = useApiAbortController()

  const isInputType = !buttonType && !buttonProps

  // Sync local options from initialOptions (and local search) — during render.
  // `prevLocalOptionsTrigger` starts null so this runs on mount (matching the
  // original effect) and again whenever the inputs change.
  const [prevLocalOptionsTrigger, setPrevLocalOptionsTrigger] = useState(null)

  if (
    prevLocalOptionsTrigger === null ||
    debouncedSearch !== prevLocalOptionsTrigger.debouncedSearch ||
    initialOptions !== prevLocalOptionsTrigger.initialOptions
  ) {
    setPrevLocalOptionsTrigger({ debouncedSearch, initialOptions })

    if (initialOptions) {
      setLocalOptions(
        localSearch
          ? filterLocalSearchItems(debouncedSearch, initialOptions)
          : initialOptions
      )
    }
  }

  // Sync the search field from inputValue while open — during render
  // (isOpen starts false, so nothing runs on mount).
  const [prevSearchInputTrigger, setPrevSearchInputTrigger] = useState({
    inputValue,
    isOpen,
  })

  if (
    inputValue !== prevSearchInputTrigger.inputValue ||
    isOpen !== prevSearchInputTrigger.isOpen
  ) {
    setPrevSearchInputTrigger({ inputValue, isOpen })

    if (isOpen) {
      setSearch(inputValue)
    }
  }

  // TODO Use floating ui
  useEffect(() => {
    if (selectRef && selectRef.current && isOpen && isInitiated) {
      updateDropdownRect()
    }
    // search for  updateRect when select has minSearchLength and when scrolling
  }, [count, isInitiated, isOpen, search, selectRef, updateRect])

  // TODO Replace logic
  // Sync the search field from initSearchValue while open — during render
  // (isOpen starts false, so nothing runs on mount).
  const [prevInitSearchTrigger, setPrevInitSearchTrigger] = useState({
    initSearchValue,
    isOpen,
  })

  if (
    initSearchValue !== prevInitSearchTrigger.initSearchValue ||
    isOpen !== prevInitSearchTrigger.isOpen
  ) {
    setPrevInitSearchTrigger({ initSearchValue, isOpen })

    if (isOpen && (initSearchValue || inputValue)) {
      setSearch(initSearchValue === undefined ? inputValue : initSearchValue)
    }
  }

  // Reset select state on close. Runs only on `isOpen` change (skips the
  // initial mount, like the former `useUpdate`). Must NOT run every render:
  // otherwise `setOptions(initialOptions)` with an unstable `options` prop
  // loops infinitely (e.g. while dragging in DocFlowEditSequenceModal).
  const isSelectMountRef = useRef(true)

  useEffect(() => {
    if (isSelectMountRef.current) {
      isSelectMountRef.current = false
    } else if (!isOpen) {
      apiAbortController.current?.abort()

      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset select state on close
      setSearch(EMPTY_STRING)
      setOptions(initialOptions)
      setRect(null)
      setMeta(null)
      setLoading(false)
      setInitiated(false)
      setListHeight(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Auto open if button type
  useEffect(() => {
    if (autoFocus && !isInputType) {
      buttonRef.current.focus()

      handleMenuOpen()
    }
  }, [])

  useEffect(() => {
    if (loadPage && !isLoading) {
      getData({ page: loadPage })
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs select state (options/search/page) with its props
      setLoadPage(null)
    }
  }, [loadPage])

  function updateDropdownRect() {
    setRect(selectRef.current.getBoundingClientRect())
  }

  function handleToggle(event) {
    stopPropagation(event)

    if (isOpen) {
      handleMenuClose()
    } else {
      handleMenuOpen()
    }
  }

  const listOptions = useMemo(
    () => (api ? options : localOptions || EMPTY_LIST),
    [api, localOptions, options]
  )

  function getData(requestParams = { page: 1 }) {
    apiAbortController.current?.abort() // Cancel request if abort controller exists

    apiAbortController.current = new AbortController()

    const params = { ...apiParams, ...requestParams }
    const requestOptions = {
      signal: apiAbortController.current.signal,
    }

    setLoading(true)
    api(debouncedSearch, params, requestOptions).then(data => {
      if (data) {
        const nextOptions =
          requestParams.page === 1
            ? getApiData(data)
            : options.concat(getApiData(data))

        setOptions(initialOptions.concat(nextOptions))
        setMeta(getApiMeta(data, params))

        setInitiated(true)
        setLoading(false)
      }
    })
  }

  // TODO Check static options search possibility
  useEffect(() => {
    if (
      api &&
      isOpen &&
      checkNeedLoadDataOnOpen({
        debouncedSearch,
        minSearchString,
        shouldLoadOnEmptyString,
      })
    ) {
      getData()
    }
  }, [api, debouncedSearch])

  function handleMenuScroll(e) {
    if (isLoading || !meta || meta.page_count === meta.page) {
      return
    }

    const { offsetHeight, scrollHeight, scrollTop } = e.target
    const scrolled = offsetHeight + scrollTop

    if (scrollHeight / 2 <= scrolled) {
      const page = meta.page + 1

      setLoadPage(page)
    }
  }

  function handleChangeSearch(e) {
    setSearch(e.target.value)

    onChangeInput?.(e.target.value)
  }

  function handleOpen(event) {
    stopPropagation(event)

    if (openOnFocus) {
      handleMenuOpen()
    }
  }

  function getButtonName() {
    if (!showLabel) {
      return placeholder
    }

    if (listOptions?.length > 0 && activeItems?.length > 0) {
      return listOptions
        .filter(option => activeItems.includes(getValue(option)))
        .map(item => getLabel(item))
        .join(', ')
    }

    if (newItem && newItem.value) {
      return newItem.value
    }

    return placeholder
  }

  function handleClear(e) {
    stopPropagation(e)

    setSearch(EMPTY_STRING)

    onClearInput?.()
  }

  function handleMenuOpen() {
    setOpen(true)

    if (
      api &&
      checkNeedLoadDataOnOpen({
        debouncedSearch,
        minSearchString,
        shouldLoadOnEmptyString,
      })
    ) {
      getData()
    } else {
      setInitiated(true)
    }
  }

  function handleDropdownClose() {
    setOpen(false)
  }

  function handleMenuClose(option) {
    handleDropdownClose()

    onClose?.(option)
  }

  function handleClickExtraItem() {
    extraItemProps?.onClick?.(handleDropdownClose, getData)
  }

  const wrapperProps = getQaSelector({ value: name })

  const showListDropdown = useMemo(
    () =>
      checkShowListDropdown({
        debouncedSearch,
        listOptions,
        minSearchString,
        search,
      }),
    [
      checkShowListDropdown,
      debouncedSearch,
      listOptions,
      minSearchString,
      search,
    ]
  )

  const showClearButton = useMemo(() => {
    return (
      (!isOpen && searchKey && (inputValue || search) && clearable) || canClear
    )
  }, [canClear, clearable, inputValue, isOpen, search, searchKey])

  const showValueItem = !isOpen && value && ValueItem

  const buttonClass = classNames(
    styles.button,
    {
      [buttonOpenClassName]:
        (!buttonType || buttonType === 'outlined') && isOpen,
    },
    buttonClassName
  )
  const selectClassName = classNames(
    styles.select,
    { [styles.open]: isOpen && actionsMenu },
    className
  )

  const preparedExtraItemProps = extraItemProps
    ? { ...extraItemProps, onClick: handleClickExtraItem }
    : null

  const inputClassName = classNames(
    { [styles.inputHidden]: showValueItem },
    { [styles.input]: !showValueItem }
  )

  return (
    <div {...wrapperProps} className={selectClassName} id={id} ref={selectRef}>
      {isInputType ? (
        <InputField
          afterButton={
            <ButtonArrow
              className={styles.openIconButton}
              isOpen={isOpen}
              onClick={handleToggle}
            />
          }
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          before={inputBefore}
          beforeButton={
            showValueItem ? (
              <SelectValueItem
                {...valueItemProps}
                Component={ValueItem}
                getLabel={getLabel}
                listIcon={listIcon}
                value={value}
                onClick={handleMenuOpen}
              />
            ) : null
          }
          borderless={inputType === 'borderless'}
          count={count}
          disabled={disabled}
          errorText={isOpen ? null : errorText}
          height={inputHeight}
          info={inputInfo}
          inline={inline}
          inputProps={ValueItem && value ? { className: inputClassName } : null}
          inputTooltip={inputTooltip}
          label={inputLabel}
          name={name}
          placeholder={value && !isMulti ? EMPTY_STRING : placeholder}
          qaName={name}
          required={required}
          showError={showError}
          value={isOpen ? search : inputValue}
          onChange={searchKey && !onChange ? handleChangeSearch : onChange}
          onClear={showClearButton ? handleClear : null}
          onClick={onClick}
          onClickBefore={handleMenuOpen}
          onFocus={handleOpen}
        />
      ) : (
        <Button
          {...buttonProps}
          afterIconProps={{
            Icon: !beforeIconProps?.Icon ? IconChevron : null,
            className: styles.afterIconContainer,
            height: ICON_HEIGHT_16,
            iconProps: {
              direction: getVerticalIconChevronDirection(isOpen),
            },
          }}
          beforeIconProps={beforeIconProps}
          className={buttonClass}
          disabled={disabled}
          elementRef={buttonRef}
          entity={entity}
          error={error}
          height={buttonHeight}
          inputValue={isOpen ? search : inputValue}
          isOpen={isOpen}
          isSkeleton={isSkeleton}
          listType={listType}
          qaName={qaName}
          showTextTooltip={showBtnTextTooltip}
          tags={tags}
          text={getButtonName()}
          textClassName={textClassName}
          tooltip={selectBtnTooltip}
          type={buttonType}
          onChange={searchKey && !onChange ? handleChangeSearch : onChange}
          onClear={showClearButton ? handleClear : null}
          onClick={handleToggle}
          onClickBefore={handleMenuOpen}
          onCloseTag={onClear || onClick}
          onUpdateDropdownRect={updateDropdownRect}
        />
      )}
      {isOpen && rect ? (
        <Dropdown
          className={dropdownClassName}
          isDropdownBottomPosition={isDropdownBottomPosition}
          maxHeight={listHeight}
          maxWidth={dropdownMaxWidth}
          parentRef={selectRef}
          qaName={qaName}
          rect={rect}
          onClose={handleMenuClose}
        >
          {showListDropdown ? (
            <SelectList
              ExtraItem={ExtraItem}
              ListItem={ListItem}
              activeItems={activeItems}
              buttonType={buttonType}
              entity={entity}
              extraItemProps={preparedExtraItemProps}
              getLabel={getLabel}
              getListItemProps={getListItemProps}
              getValue={getValue}
              isInitiated={isInitiated}
              isInputType={isInputType}
              isLoading={isLoading}
              isMulti={isMulti}
              itemContentClassName={itemContentClassName}
              listIcon={listIcon}
              listType={listType}
              localSearch={localSearch}
              maxHeight={maxHeight}
              menuClassName={menuClassName}
              noDataText={noDataText}
              options={listOptions}
              optionsCount={meta?.total ?? listOptions.length}
              placeholder={placeholder}
              scrollListener={scrollListener}
              search={search}
              searchKey={searchKey}
              showListTooltip={showListTooltip}
              onChangeSearch={handleChangeSearch}
              onClick={onClick}
              onCloseMenu={handleMenuClose}
              onEdit={onEdit}
              onScroll={handleMenuScroll}
              onSetListHeight={setListHeight}
            />
          ) : null}
        </Dropdown>
      ) : null}
    </div>
  )
}

export default CustomSelect
